import { keepPreviousData, QueryClient, QueryKey, useQuery } from '@tanstack/react-query'
import { SupportedChainId } from 'config/chains'
import { useConnection } from 'hooks/useConnection'
import qs from 'qs'
import { useMemo } from 'react'
import { ExecutionExplanation, QuoteResult } from 'state/routing/trades'

import type { BackendQueryOptions } from './api'
import { fetchBackendJson } from './api'

type QuoteQueryOptions<TData = QuoteResult, TQueryFnData = TData, TError = Error> = BackendQueryOptions<
  TData,
  TQueryFnData,
  TError
>

type QuoteProvidersResponse = { providers: string[] }

export type QuoteQueryParams = {
  tokenInAddress?: string
  tokenInChainId?: SupportedChainId
  tokenOutAddress?: string
  tokenOutChainId?: SupportedChainId
  amount?: string
  type?: 'exactIn' | 'exactOut'
  recipient?: string
  slippageTolerance?: number
  refCode?: number
  providers?: string
}

type NormalizedQuoteParams = Required<
  Pick<
    QuoteQueryParams,
    | 'tokenInAddress'
    | 'tokenInChainId'
    | 'tokenOutAddress'
    | 'tokenOutChainId'
    | 'amount'
    | 'type'
    | 'slippageTolerance'
    | 'refCode'
    | 'providers'
  >
> &
  Pick<QuoteQueryParams, 'recipient'>

const routingKeyBase = ['routing'] as const
export const routingQuoteKeyBase = [...routingKeyBase, 'quote'] as const
export const kxRouterQuoteKeyBase = ['kxRouterQuote'] as const

export const routingQueryKeys = {
  providers: (chainId: number) => [...routingKeyBase, 'providers', chainId] as const,
  quote: (params: NormalizedQuoteParams) =>
    [
      ...routingQuoteKeyBase,
      params.tokenInChainId,
      params.tokenInAddress.toLowerCase(),
      params.tokenOutChainId,
      params.tokenOutAddress.toLowerCase(),
      params.amount,
      params.type,
      params.recipient?.toLowerCase() ?? null,
      params.slippageTolerance,
      params.refCode,
      params.providers,
    ] as const,
}

export async function clearKxQuoteCache(queryClient: QueryClient): Promise<void> {
  await Promise.all([
    queryClient.cancelQueries({ queryKey: routingQuoteKeyBase }),
    queryClient.cancelQueries({ queryKey: kxRouterQuoteKeyBase }),
  ])

  queryClient.removeQueries({ queryKey: routingQuoteKeyBase })
  queryClient.removeQueries({ queryKey: kxRouterQuoteKeyBase })
}

const normalizeQuoteParams = (params: QuoteQueryParams): NormalizedQuoteParams | null => {
  const {
    tokenInAddress,
    tokenInChainId,
    tokenOutAddress,
    tokenOutChainId,
    amount,
    type,
    recipient,
    slippageTolerance,
    refCode,
    providers,
  } = params

  if (
    !tokenInAddress ||
    tokenInChainId === undefined ||
    !tokenOutAddress ||
    tokenOutChainId === undefined ||
    amount === undefined ||
    !type ||
    slippageTolerance === undefined ||
    refCode === undefined ||
    providers === undefined
  ) {
    return null
  }

  return {
    tokenInAddress,
    tokenInChainId,
    tokenOutAddress,
    tokenOutChainId,
    amount,
    type,
    recipient,
    slippageTolerance,
    refCode,
    providers,
  }
}

const buildQuoteQuery = (params: NormalizedQuoteParams) =>
  qs.stringify({
    tokenInAddress: params.tokenInAddress,
    tokenInChainId: params.tokenInChainId,
    tokenOutAddress: params.tokenOutAddress,
    tokenOutChainId: params.tokenOutChainId,
    amount: params.amount,
    type: params.type,
    recipient: params.recipient,
    slippageTolerance: params.slippageTolerance,
    refCode: params.refCode,
    providers: params.providers,
  })

const logQuoteExplanations = (data: QuoteResult | undefined) => {
  if (!data?.expl) return
  console.log('=== Quotes ===')

  const quotesInfo = data.expl
    .map((explItem: ExecutionExplanation, index) => {
      const provider = explItem.provider || 'Unknown'
      const quote = explItem.output || 'NA'
      const status = explItem.status || 'NA'
      const priority = explItem.priority || 'NA'
      const gas = explItem.gas || 'NA'
      const executionTime = explItem.executionTime || 'NA'
      const error = explItem.error || 'NA'
      const simulationPassed = explItem.simulationPassed || 'NA'
      const simulationError = explItem.simulationError || 'NA'
      const simulationTime = explItem.simulationTime || 'NA'
      const simulatedAmountOut = explItem.simulatedAmountOut || 'NA'
      const simulationDeviationBps = explItem.simulationDeviationBps || 'NA'

      const isBest = data.provider === provider ? '(best price)' : ''

      return `${provider}${isBest}: ${quote}, status: ${status}, priority: ${priority}, gas: ${gas}, executionTime: ${executionTime}, error: ${error}, simulationPassed: ${simulationPassed}, simulationError: ${simulationError}, simulationTime: ${simulationTime}, simulatedAmountOut: ${simulatedAmountOut}, simulationDeviationBps: ${simulationDeviationBps} ${
        index !== data.expl.length - 1 ? '\n' : ''
      }`
    })
    .join('\n')

  console.log(`quotes {\n${quotesInfo}\n}`)
  console.log('=== End Quotes ===\n')
}

const fetchQuote = async (params: NormalizedQuoteParams, signal?: AbortSignal): Promise<QuoteResult> => {
  const query = buildQuoteQuery(params)
  const data = await fetchBackendJson<QuoteResult>(`/quote?${query}`, { method: 'GET', signal })
  logQuoteExplanations(data)
  return data
}

const fetchQuoteProviders = async (chainId: number, signal?: AbortSignal): Promise<QuoteProvidersResponse> => {
  return fetchBackendJson<QuoteProvidersResponse>(`/quote/providers?chainId=${chainId}`, { method: 'GET', signal })
}

export const useRouteQuote = (params: QuoteQueryParams, options?: QuoteQueryOptions<QuoteResult>) => {
  const normalizedParams = useMemo(
    () => normalizeQuoteParams(params),
    [
      params.tokenInAddress,
      params.tokenInChainId,
      params.tokenOutAddress,
      params.tokenOutChainId,
      params.amount,
      params.type,
      params.recipient,
      params.slippageTolerance,
      params.refCode,
      params.providers,
    ]
  )

  const {
    skip,
    enabled: optionEnabled,
    queryKey,
    retry = false,
    refetchOnWindowFocus = false,
    staleTime = 45 * 1000,
    gcTime = 45 * 1000,
    placeholderData = keepPreviousData,
    ...queryOptions
  } = options ?? {}

  const enabled = (optionEnabled ?? true) && !skip && Boolean(normalizedParams)

  return useQuery<QuoteResult, Error>({
    queryKey: (queryKey ?? (normalizedParams ? routingQueryKeys.quote(normalizedParams) : routingKeyBase)) as QueryKey,
    queryFn: ({ signal }) => {
      if (!normalizedParams) throw new Error('Invalid quote params')
      return fetchQuote(normalizedParams, signal)
    },
    enabled,
    retry,
    refetchOnWindowFocus,
    staleTime,
    gcTime,
    placeholderData,
    ...queryOptions,
  })
}

export const useQuoteProviders = (options?: QuoteQueryOptions<QuoteProvidersResponse>) => {
  const { chainId } = useConnection()
  const {
    skip,
    enabled: optionEnabled,
    queryKey,
    retry = false,
    refetchOnWindowFocus = false,
    refetchOnReconnect = false,
    refetchInterval = false,
    ...queryOptions
  } = options ?? {}
  const enabled = (optionEnabled ?? true) && !skip

  return useQuery<QuoteProvidersResponse, Error>({
    queryKey: (queryKey ?? routingQueryKeys.providers(chainId)) as QueryKey,
    queryFn: ({ signal }) => fetchQuoteProviders(chainId, signal),
    enabled,
    retry,
    refetchOnWindowFocus,
    refetchOnReconnect,
    refetchInterval,
    staleTime: Infinity,
    ...queryOptions,
  })
}
